import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { onRoomsChanged } from '../lib/roomsEvents';
import { createRoom, deleteRoom, listMembers, listRooms, updateRoom, type ChatRoomDto } from '../api/chats';
import { uploadMedia } from '../api/media';
import { ApiError } from '../api/client';
import type { ChatSummary, UserDto } from '../api/types';
import Avatar from './Avatar';
import RoomAvatar from './RoomAvatar';
import AvatarEditor from './AvatarEditor';
import ConfirmModal from './ConfirmModal';
import { backdropClose } from '../lib/modalBackdrop';

interface Props {
  chat: ChatSummary;
  me: UserDto | null;
  roomId: string | null;
  onRoomChange: (roomId: string | null) => void;
  /** Мобильный сценарий — стрелка "назад" к списку чатов (на десктопе .back-btn скрыта
   *  CSS-ом, см. theme.css). Десктоп переключается кликом по другому чату на свёрнутом
   *  рельсе (см. отчёт пользователя), отдельная кнопка там не нужна. */
  onBack: () => void;
}

/** Форма создания/редактирования комнаты — своё модальное окно вместо инлайн-редактирования
 *  прямо в панели переключения (см. отчёт пользователя и общий паттерн проекта: «×» = применить
 *  и закрыть, вторичные действия — не захламляют саму панель). */
export function RoomEditModal({ chatId, room, onClose, onCreated, onUpdated }: {
  chatId: string;
  room: ChatRoomDto | null;
  onClose: () => void;
  onCreated: (r: ChatRoomDto) => void;
  onUpdated: (r: ChatRoomDto) => void;
}) {
  const { t } = useTranslation('rooms');
  const [name, setName] = useState(room?.name ?? '');
  // undefined — аватар не трогаем (только при редактировании); null — сбросить; строка — новый media id.
  const [avatarId, setAvatarId] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const previewAvatarId = avatarId === undefined ? (room?.avatarFileId ?? null) : avatarId;
  // Файл ждёт кропа в AvatarEditor — раньше грузился как есть без возможности подогнать кадр
  // (см. отчёт пользователя "при редактировании аватарки комнаты нет возможности настроить
  // аватарку"), теперь тот же кроп-редактор, что и у аватара группы/пользователя.
  const [pendingAvatarFile, setPendingAvatarFile] = useState<File | null>(null);

  const onAvatarCropped = async (blob: Blob) => {
    setPendingAvatarFile(null);
    try {
      const m = await uploadMedia(blob, 'avatar.jpg');
      setAvatarId(m.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('uploadRoomPhotoFailed'));
      setPendingAvatarFile(new File([blob], 'avatar.jpg', { type: blob.type || 'image/jpeg' }));
    }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    try {
      if (room) {
        const patch: { name?: string; avatarFileId?: string | null; clearAvatar?: boolean } = { name: trimmed };
        if (avatarId === null) patch.clearAvatar = true;
        else if (avatarId) patch.avatarFileId = avatarId;
        onUpdated(await updateRoom(chatId, room.id, patch));
      } else {
        onCreated(await createRoom(chatId, trimmed, avatarId ?? null));
      }
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('saveRoomFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" {...backdropClose(save)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 340 }}>
        <button type="button" className="modal-close" title={t('done')} onClick={save}>✕</button>
        <h2>{room ? t('room') : t('newRoom')}</h2>
        {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          <label style={{ cursor: 'pointer' }} title={t('roomAvatar')}>
            <RoomAvatar avatarFileId={previewAvatarId} name={name || '?'} size={64} />
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) setPendingAvatarFile(f);
              }}
            />
          </label>
          {previewAvatarId && (
            <button type="button" className="link-btn" onClick={() => setAvatarId(null)}>{t('removeAvatar')}</button>
          )}
        </div>
        <input
          className="input" autoFocus placeholder={t('roomName')} maxLength={64}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
        />
      </div>
      {pendingAvatarFile && (
        <AvatarEditor
          file={pendingAvatarFile}
          onCancel={() => setPendingAvatarFile(null)}
          onSave={onAvatarCropped}
        />
      )}
    </div>
  );
}

/** Колонка комнат (тем) группового чата — отдельная от ленты сообщений, показывается вместо
 *  прежнего горизонтального ряда «таблеток» под шапкой (см. отчёт пользователя "комнаты в
 *  групповых чатах надо переделать... в свободном месте отобразить комнаты"). Появляется
 *  рядом со свёрнутым до аватарок списком чатов (см. App.tsx/ChatList collapsed) — место для
 *  неё освобождает именно список чатов, а не лента. */
export default function RoomsColumn({ chat, me, roomId, onRoomChange, onBack }: Props) {
  const { t } = useTranslation('rooms');
  const chatId = chat.id;
  const [rooms, setRooms] = useState<ChatRoomDto[]>([]);
  const [myRole, setMyRole] = useState<number | null>(null);
  const [menuRoomId, setMenuRoomId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<ChatRoomDto | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Комната создана из «Информации о группе» — перечитываем список (см. lib/roomsEvents).
  useEffect(
    () => onRoomsChanged((id) => {
      if (id === chatId) listRooms(chatId).then(setRooms).catch(() => {});
    }),
    [chatId],
  );

  useEffect(() => {
    let alive = true;
    setRooms([]);
    setMyRole(null);
    // Модалки/меню комнаты держат id из ПРЕЖНЕГО чата — при переключении между двумя
    // группами-с-комнатами без их закрытия операция иначе уйдёт по chatId нового чата,
    // но с room.id старого (см. отчёт ревью).
    setMenuRoomId(null);
    setEditTarget(null);
    setDeleteId(null);
    listRooms(chatId).then((r) => alive && setRooms(r)).catch(() => {});
    listMembers(chatId)
      .then((list) => {
        if (!alive || !me) return;
        const m = list.find((mm) => mm.user.id === me.id);
        if (m) setMyRole(m.role);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, me?.id]);

  const canManage = myRole != null && myRole <= 2;
  const unreadForRoom = (id: string | null) => chat.roomUnread?.find((entry) => entry.roomId === id)?.count ?? 0;

  const removeRoom = async (id: string) => {
    try {
      await deleteRoom(chatId, id);
      setRooms((prev) => prev.filter((r) => r.id !== id));
      if (roomId === id) onRoomChange(null);
    } catch {
      /* не критично */
    }
  };

  return (
    <aside className="sidebar rooms-column">
      <div className="rooms-column-head">
        <button type="button" className="back-btn" title={t('backToChats')} onClick={onBack}>
          <img src="/back.svg" alt={t('back')} />
        </button>
        <Avatar
          userId={chat.otherUserId}
          chatId={chat.type === 2 || !chat.otherUserId ? chat.id : null}
          name={chat.title || '?'}
          version={chat.avatarFileId}
          size={26}
        />
        <span className="rooms-column-title">{chat.title || t('chat')}</span>
      </div>
      <div className="rooms-column-list">
        <button type="button" className={'room-row' + (roomId === null ? ' active' : '')} onClick={() => onRoomChange(null)}>
          <span className="room-row-icon hash">#</span>
          <span className="room-row-name">{t('general')}</span>
          {unreadForRoom(null) > 0 && <span className="unread">{unreadForRoom(null) > 999 ? '999+' : unreadForRoom(null)}</span>}
        </button>
        {rooms.map((r) => (
          <div key={r.id} className="room-row-wrap">
            <button type="button" className={'room-row' + (roomId === r.id ? ' active' : '')} onClick={() => onRoomChange(r.id)}>
              <RoomAvatar avatarFileId={r.avatarFileId} name={r.name} size={24} />
              <span className="room-row-name">{r.name}</span>
              {unreadForRoom(r.id) > 0 && <span className="unread">{unreadForRoom(r.id) > 999 ? '999+' : unreadForRoom(r.id)}</span>}
            </button>
            {canManage && (
              <div className="room-row-menu-wrap">
                <button
                  type="button"
                  className="room-row-menu-btn"
                  title={t('more')}
                  onClick={() => setMenuRoomId((v) => (v === r.id ? null : r.id))}
                >
                  ⋮
                </button>
                {menuRoomId === r.id && (
                  <>
                    <div className="menu-backdrop" onClick={() => setMenuRoomId(null)} />
                    <div className="menu room-row-menu">
                      <button type="button" className="menu-item" onClick={() => { setMenuRoomId(null); setEditTarget(r); }}>
                        {t('renameOrChangeAvatar')}
                      </button>
                      <button type="button" className="menu-item danger" onClick={() => { setMenuRoomId(null); setDeleteId(r.id); }}>
                        {t('delete')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {editTarget && (
        <RoomEditModal
          chatId={chatId}
          room={editTarget}
          onClose={() => setEditTarget(null)}
          onCreated={(r) => setRooms((prev) => [...prev, r])}
          onUpdated={(r) => setRooms((prev) => prev.map((x) => (x.id === r.id ? r : x)))}
        />
      )}

      {deleteId != null && (
        <ConfirmModal
          message={t('deleteRoomConfirmation', { name: rooms.find((r) => r.id === deleteId)?.name ?? '' })}
          onConfirm={() => {
            removeRoom(deleteId);
            setDeleteId(null);
          }}
          onCancel={() => setDeleteId(null)}
        />
      )}
    </aside>
  );
}
